import { useState, useEffect } from 'react';

interface OperatorModeState {
  /** True when running as installed PWA (standalone display mode). */
  isStandalone: boolean;
  /** True when viewport is at or below mobile breakpoint (768px). */
  isMobile: boolean;
  /** True when Operator shell should render (standalone OR mobile). */
  isOperator: boolean;
  /** True when touch is the primary input. */
  isTouch: boolean;
  /** True when demo mode is active (?demo=1 or localStorage). */
  isDemoMode: boolean;
}

const MOBILE_BREAKPOINT = 768;
const DEMO_STORAGE_KEY = 'operator.demo';

function getIsStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

function getIsMobile(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= MOBILE_BREAKPOINT;
}

function getIsTouch(): boolean {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

function getIsDemoMode(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get('demo') === '1') {
    try { localStorage.setItem(DEMO_STORAGE_KEY, '1'); } catch { /* */ }
    return true;
  }
  try { return localStorage.getItem(DEMO_STORAGE_KEY) === '1'; } catch { return false; }
}

export function useOperatorMode(): OperatorModeState {
  const [isStandalone, setIsStandalone] = useState(getIsStandalone);
  const [isMobile, setIsMobile] = useState(getIsMobile);
  const isTouch = getIsTouch();
  const isDemoMode = getIsDemoMode();

  useEffect(() => {
    const standaloneQuery = window.matchMedia('(display-mode: standalone)');
    const handleStandalone = (e: MediaQueryListEvent) => setIsStandalone(e.matches);
    standaloneQuery.addEventListener('change', handleStandalone);

    const handleResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', handleResize);

    return () => {
      standaloneQuery.removeEventListener('change', handleStandalone);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const isOperator = isStandalone || isMobile;

  return { isStandalone, isMobile, isOperator, isTouch, isDemoMode };
}
